// Catalog.ID Client — Encrypted Key Bag Bundle (format v1)
// ============================================================================
// Self-describing, portable export of the member's encrypted key bag, per
// Recovery Kit Addendum §2.4/§5. The bundle is opaque ciphertext to anyone
// without the supersecret and is meant to be stored abundantly (devices,
// cloud, custodians). Each export re-encrypts the key bag under a FRESH
// per-bundle Argon2id salt (§5.1) so work cannot be amortized between a
// captured bundle and the server replica.
//
// Field names are snake_case: the file is an archival artifact whose format
// is defined by the addendum, not by this client's wire conventions.
//
// ── Account blobs (cid-encrypted-storage §4.2) ──────────────────────────────
// The bundle also carries the account's blobs. They are NOT re-encrypted under
// the bundle key: each stays exactly as the server stored it, under a key
// derived from `derive_root` — which this bundle already carries as an ordinary
// bag entry. So one supersecret opens the bag, the bag yields the root, and the
// root re-derives every blob key. Re-encrypting them would buy nothing (the
// bundle is already opaque without the supersecret) and would cost a second
// code path that could disagree with the live one about what a blob key is.
//
// The section is REQUIRED, even when empty. A bundle with no `blobs` field
// would restore silently to an account with no application state, which is the
// exact loss the section exists to prevent — so it is refused instead. Pre-live
// there are no bundles in the wild to be compatible with (§ rulings, rev 2), so
// `format_version` stays 1.

import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import * as aes from './aes';
import { ARGON2_PARAMS, deriveKeyFromSupersecret } from './argon2';
import { encryptKeyBag, decryptEntry, type KeyBag } from './keybag';

export const BUNDLE_FORMAT = 'catalog-id-keybag-bundle';
export const BUNDLE_FORMAT_VERSION = 1;

export interface BundleEntry {
    key_id: string;
    key_index: number;
    algorithm: string;
    /** base64 AES-256-GCM nonce */
    nonce: string;
    /** base64 ciphertext (auth tag included) of JSON { algorithm, privateKey } */
    encrypted_private_key: string;
}

export interface BundleKeyChainLink {
    key_id: string;
    key_index: number;
    algorithm: string;
    /** base64 public key */
    public_key: string;
    status: string;
}

/**
 * One account blob, verbatim as the server stored it. Opaque here: the
 * ciphertext is AES-256-GCM under `HKDF(derive_root, …)`, which nothing in this
 * file can or should open.
 */
export interface BundleBlob {
    /** `<moduleId>/<name>`. */
    blob_key: string;
    generation: number;
    /** base64 96-bit nonce. */
    nonce: string;
    /** base64 ciphertext (auth tag included). */
    ciphertext: string;
}

export interface KeyBagBundle {
    format: typeof BUNDLE_FORMAT;
    format_version: typeof BUNDLE_FORMAT_VERSION;
    /** Binds the bundle to an account without naming it: hex SHA-256 of a domain-separated CID hash. */
    account_fingerprint: string;
    /** base64 — fresh per-bundle salt, distinct from the account salt (§5.1). */
    argon2id_salt: string;
    argon2id_params: {
        memory_kib: number;
        iterations: number;
        parallelism: number;
        hash_length: number;
    };
    created_at: string;
    entries: BundleEntry[];
    key_chain: BundleKeyChainLink[];
    /**
     * The account's blobs, as stored (cid-encrypted-storage §4.2). Required —
     * an empty array for an account that has none. See the module header.
     */
    blobs: BundleBlob[];
    /** hex SHA-256 over the canonical bundle bytes (fingerprint field excluded). */
    bundle_fingerprint: string;
}

const ACCOUNT_FINGERPRINT_DOMAIN = 'Catalog.ID account fingerprint v1';

export async function accountFingerprint(cid: string): Promise<string> {
    return aes.toHex(await aes.sha256(aes.encodeUtf8(ACCOUNT_FINGERPRINT_DOMAIN + cid)));
}

export interface ExportBundleParams {
    cid: string;
    bag: KeyBag;
    supersecret: string;
    /** Public half of every key, for the key_chain section. */
    publicKeys: { keyId: string; algorithm: string; publicKey: string; keyIndex?: number; status?: string }[];
    /** ISO 8601 creation timestamp; caller supplies (testability). */
    createdAt: string;
    /**
     * The account's blobs, fetched by the caller — `collectBlobsForBundle` in
     * `bundle-blobs.ts` does it in one call. Required (pass `[]` for an account
     * with none) so no export site can forget the section by omission.
     */
    blobs: BundleBlob[];
}

/**
 * Export the key bag as a bundle. Re-encrypts every entry under a fresh
 * Argon2id salt derived from the same supersecret.
 */
export async function exportKeyBagBundle(params: ExportBundleParams): Promise<KeyBagBundle> {
    const encrypted = await encryptKeyBag(params.bag, params.supersecret);

    const unsigned: Omit<KeyBagBundle, 'bundle_fingerprint'> = {
        format: BUNDLE_FORMAT,
        format_version: BUNDLE_FORMAT_VERSION,
        account_fingerprint: await accountFingerprint(params.cid),
        argon2id_salt: encrypted.keySalt,
        argon2id_params: {
            memory_kib: ARGON2_PARAMS.memorySize,
            iterations: ARGON2_PARAMS.iterations,
            parallelism: ARGON2_PARAMS.parallelism,
            hash_length: ARGON2_PARAMS.hashLength,
        },
        created_at: params.createdAt,
        entries: encrypted.entries.map(e => ({
            key_id: e.keyId,
            key_index: e.keyIndex,
            algorithm: e.algorithm,
            nonce: e.nonce,
            encrypted_private_key: e.encryptedPrivateKey,
        })),
        blobs: params.blobs,
        key_chain: params.publicKeys.map(k => ({
            key_id: k.keyId,
            key_index: k.keyIndex ?? 0,
            algorithm: k.algorithm,
            public_key: k.publicKey,
            status: k.status ?? 'active',
        })),
    };

    return { ...unsigned, bundle_fingerprint: await computeBundleFingerprint(unsigned) };
}

/**
 * Fingerprint = hex SHA-256 over the RFC 8785 canonical JSON of the bundle
 * with the `bundle_fingerprint` field excluded.
 */
export async function computeBundleFingerprint(bundle: Omit<KeyBagBundle, 'bundle_fingerprint'>): Promise<string> {
    const { ...copy } = bundle as Record<string, unknown>;
    delete copy['bundle_fingerprint'];
    return aes.toHex(await aes.sha256(canonicalize(copy as CanonicalValue)));
}

export interface DecryptedBundle {
    bag: KeyBag;
    keyChain: BundleKeyChainLink[];
    /** The bundle's blobs, still encrypted under derive-root-derived keys. The
     *  unlock path pushes the ones the server lacks — see `bundle-blobs.ts`. */
    blobs: BundleBlob[];
}

/**
 * Decrypt a bundle with the supersecret. Verifies the bundle fingerprint and
 * (when `expectedCid` is given) the account binding before deriving the key.
 * Restore path for Recovery Flow step 3-4 (§15); also the round-trip check
 * used at export time.
 */
export async function decryptKeyBagBundle(
    bundle: KeyBagBundle,
    supersecret: string,
    expectedCid?: string,
): Promise<DecryptedBundle> {
    if (bundle.format !== BUNDLE_FORMAT || bundle.format_version !== BUNDLE_FORMAT_VERSION) {
        throw new Error(`Unsupported bundle format: ${bundle.format} v${bundle.format_version}`);
    }
    // Shape before integrity: a bundle with no `blobs` section would otherwise
    // fail as a fingerprint mismatch, which reads as "corrupted file" when the
    // truth is "this file predates account blobs and would restore your
    // identity without your application state".
    if (!Array.isArray(bundle.blobs)) {
        throw new Error(
            'This backup has no blobs section — it was written before account blobs '
            + 'existed and cannot restore them');
    }
    const fingerprint = await computeBundleFingerprint(bundle);
    if (fingerprint !== bundle.bundle_fingerprint) {
        throw new Error('Bundle fingerprint mismatch — the file is corrupted or was modified');
    }
    if (expectedCid !== undefined && await accountFingerprint(expectedCid) !== bundle.account_fingerprint) {
        throw new Error('This bundle belongs to a different Catalog.ID account');
    }
    if (
        bundle.argon2id_params.memory_kib !== ARGON2_PARAMS.memorySize
        || bundle.argon2id_params.iterations !== ARGON2_PARAMS.iterations
        || bundle.argon2id_params.parallelism !== ARGON2_PARAMS.parallelism
        || bundle.argon2id_params.hash_length !== ARGON2_PARAMS.hashLength
    ) {
        // Self-describing format: honor the recorded params rather than assume
        // ours — but this client only implements one parameter set, so reject
        // anything else loudly instead of deriving a wrong key.
        throw new Error('Bundle uses Argon2id parameters this client does not support');
    }

    const derivedKey = await deriveKeyFromSupersecret(supersecret, aes.fromBase64(bundle.argon2id_salt));
    try {
        const entries = await Promise.all(bundle.entries.map(e => decryptEntry({
            keyId: e.key_id,
            keyIndex: e.key_index,
            algorithm: e.algorithm,
            nonce: e.nonce,
            encryptedPrivateKey: e.encrypted_private_key,
        }, derivedKey)));
        return { bag: { entries }, keyChain: bundle.key_chain, blobs: bundle.blobs };
    } finally {
        derivedKey.fill(0);
    }
}

/** Serialize for download. Pretty-printed: the file is an archival artifact
 *  a human may need to inspect decades from now; the fingerprint is computed
 *  over canonical bytes, so presentation whitespace is free. */
export function serializeBundle(bundle: KeyBagBundle): string {
    return JSON.stringify(bundle, null, 2) + '\n';
}

export function parseBundle(json: string): KeyBagBundle {
    const parsed = JSON.parse(json) as KeyBagBundle;
    if (parsed?.format !== BUNDLE_FORMAT) {
        throw new Error('Not a Catalog.ID key bag bundle file');
    }
    return parsed;
}

export function bundleFilename(cid: string, bundle: KeyBagBundle): string {
    return `catalog-id-keybag-bundle-${cid}-${bundle.bundle_fingerprint.slice(0, 8)}.json`;
}
